/**
 * Platform API client.
 *
 * Every request is same-origin. In production Caddy reverse proxies `/api/*` to the Platform API
 * over Railway private networking, and the Vite dev server reproduces that locally, so the browser
 * never makes a cross-origin call and the session cookie stays host-only to `app.urlshot.io`.
 *
 * There is deliberately no token handling here. The session lives in an `HttpOnly` cookie that
 * JavaScript cannot read, which is the point: an XSS bug on this origin cannot exfiltrate it. The
 * only credential this module touches is the CSRF token, which is meant to be readable.
 */
import { clearSessionHint } from './session-hint';

/** Cookie the Platform API sets for the double-submit CSRF check, and the header that echoes it. */
const CSRF_COOKIE_NAME = 'urlshot_csrf';
const CSRF_HEADER_NAME = 'x-urlshot-csrf';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/** The error envelope the Platform API returns for every failure. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    requestId: string;
    details?: { field: string; message: string }[];
  };
}

/**
 * A failed API call.
 *
 * Carries the machine-readable code so callers can branch (`validation_failed` populates form
 * errors, `unauthenticated` redirects), and the request identifier so a user reporting a problem
 * can quote a value that appears in the server logs.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string;
  readonly details: { field: string; message: string }[];

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error.code;
    this.requestId = body.error.requestId;
    this.details = body.error.details ?? [];
  }

  /** Whether the session is gone: expired, revoked, or never established. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/**
 * Read the CSRF token the server set.
 *
 * This cookie is intentionally not `HttpOnly` -- the whole double-submit pattern depends on this
 * origin being able to read it and echo it in a header, which an attacker's origin cannot do.
 */
function readCsrfToken(): string | null {
  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${CSRF_COOKIE_NAME}=`));
  const value = match?.slice(CSRF_COOKIE_NAME.length + 1);
  return value === undefined || value === '' ? null : decodeURIComponent(value);
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Call the Platform API.
 *
 * On a `401` the marketing hint cookie is cleared before the error propagates. The specification
 * requires the dashboard to clear the hint when it discovers its real session has expired or been
 * revoked; doing it here means every call site gets it, rather than each one remembering.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (options.body !== undefined) headers['content-type'] = 'application/json';

  if (!SAFE_METHODS.has(method)) {
    const csrfToken = readCsrfToken();
    if (csrfToken !== null) headers[CSRF_HEADER_NAME] = csrfToken;
  }

  const response = await fetch(`/api${path}`, {
    method,
    headers,
    // Same-origin rather than `include`: this client must never send credentials cross-origin,
    // and stating it explicitly means a future base-URL change cannot quietly start doing so.
    credentials: 'same-origin',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    if (response.status === 401) clearSessionHint();

    if (isApiErrorBody(payload)) throw new ApiError(response.status, payload);

    // A non-envelope failure means something in front of the API answered -- a proxy, or the
    // service being down. Report it in the same shape so callers have one error type.
    throw new ApiError(response.status, {
      error: {
        code: 'service_unavailable',
        message: 'The service is unavailable. Please try again.',
        requestId: '',
      },
    });
  }

  return payload as T;
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (value === null || typeof value !== 'object') return false;
  const candidate = (value as { error?: unknown }).error;
  return (
    candidate !== null &&
    typeof candidate === 'object' &&
    typeof (candidate as { code?: unknown }).code === 'string'
  );
}

/** Map an API validation failure onto the field errors a form can display. */
export function fieldErrorsOf(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  const fields: Record<string, string> = {};
  for (const detail of error.details) fields[detail.field] = detail.message;
  return fields;
}
